import _ from 'lodash';
import React, { useMemo } from 'react';
import dayjs from 'dayjs';

// Material UI
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';

// Data
import { numToTime } from '../../../lib/helpers/conversion.helpers';
import { pluralize } from '../../../lib/helpers/text.helpers';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { SportTerminology } from '../../../lib/types';
import getMessages from './disqualified-sessions-list.messages';

// Components
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';

interface IDisqualifiedSessionsList {
  sessions?: IGroupStageDisqualifiedSession[];
  warnings?: IGroupStageDisqualifiedSession[];
}

const formatSlot = (session: { date: string; start: number; duration: number }) => {
  const day = dayjs(session.date).isValid() ? dayjs(session.date).format('ddd MMM D') : session.date;
  return `${day}, ${numToTime(session.start)}–${numToTime(session.start + session.duration)}`;
};

const formatVenue = (session: IGroupStageDisqualifiedSession) => {
  const venue = _.compact([session.location, session.area]).join(' — ');
  return venue ? ` at ${venue}` : '';
};

const formatClashes = (session: IGroupStageDisqualifiedSession, prefix: string) =>
  (session.conflictsWith.length ? `${prefix}${session.conflictsWith.map(formatSlot).join(', ')}` : '');

// Two lists: ice times the round cannot use, and ice times it uses but that need a human look.
// A double-booked sheet always warns and never blocks — one of the two bookings is wrong.
export const DisqualifiedSessionsList: React.FC<IDisqualifiedSessionsList> = ({ sessions, warnings }) => {
  const termLookup = useCompanySportTerminology();
  const messages = useMemo(() => getMessages(termLookup), [termLookup]);
  const sessionTerm = termLookup[SportTerminology.SESSION].toLowerCase();

  const { overlaps, doubleBooked, assignedCount } = useMemo(() => {
    const byReason = _.groupBy(sessions || [], 'reason');
    return {
      overlaps: byReason.OVERLAPS_PREVIOUS_STAGE || [],
      doubleBooked: byReason.GROUP_DOUBLE_BOOKED || [],
      assignedCount: (byReason.ALREADY_ASSIGNED || []).length
    };
  }, [sessions]);

  const sameIceWarnings = useMemo(
    () => (warnings || []).filter((warning) => warning.reason === 'SAME_ICE_CONFLICT'),
    [warnings]
  );

  const hasBlockers = Boolean(overlaps.length || doubleBooked.length || assignedCount);
  if (!hasBlockers && !sameIceWarnings.length) return null;

  return (
    <Box mt={1}>
      {hasBlockers && (
        <>
          <Typography variant="body2">
            <strong><IntlMsg {...messages.heading} /></strong>
          </Typography>
          <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
            {overlaps.map((session) => (
              <li key={session._id}>
                <Typography variant="body2" component="span">
                  <IntlMsg
                    {...messages.overlapsPreviousStage}
                    values={{ slot: formatSlot(session), venue: formatVenue(session), clashes: formatClashes(session, ' with ') }}
                  />
                </Typography>
              </li>
            ))}
            {doubleBooked.map((session) => (
              <li key={session._id}>
                <Typography variant="body2" component="span">
                  <IntlMsg
                    {...messages.groupDoubleBooked}
                    values={{
                      slot: formatSlot(session),
                      venue: formatVenue(session),
                      session: sessionTerm,
                      clashes: formatClashes(session, ': ')
                    }}
                  />
                </Typography>
              </li>
            ))}
            {!!assignedCount && (
              <li key="already-assigned">
                <Typography variant="body2" component="span">
                  <IntlMsg
                    {...messages.alreadyAssigned}
                    values={{ count: assignedCount, noun: pluralize(assignedCount, sessionTerm) }}
                  />
                </Typography>
              </li>
            )}
          </ul>
        </>
      )}
      {!!sameIceWarnings.length && (
        <Box mt={hasBlockers ? 1 : 0}>
          <Typography variant="body2" color="error">
            <strong><IntlMsg {...messages.warningsHeading} /></strong>
          </Typography>
          <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
            {sameIceWarnings.map((session) => (
              <li key={session._id}>
                <Typography variant="body2" component="span" color="error">
                  <IntlMsg
                    {...messages.sameIceWarning}
                    values={{ slot: formatSlot(session), venue: formatVenue(session), clashes: formatClashes(session, ' with ') }}
                  />
                </Typography>
              </li>
            ))}
          </ul>
        </Box>
      )}
    </Box>
  );
};
